<script setup lang="ts">
import type { SpaceScale } from '../../../token/type/space';

type Element = 'div' | 'span' | 'li' | 'ol' | 'ul';
type Align = 'start' | 'center' | 'end' | 'space-around' | 'space-between' | 'space-evenly';
type BlockAlign = 'start' | 'center' | 'end' | 'baseline' | 'stretch';
type Direction = 'row' | 'row-reverse';

interface InlineStackProps {
  as?: Element;
  children?: any;
  align?: Align;
  direction?: Direction;
  blockAlign?: BlockAlign;
  gap?: SpaceScale;
  wrap?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
}

const props = defineProps<InlineStackProps>();

const as = props.as ?? 'div';
const wrap = props.wrap ?? true;

const flexGap = computed(() => {
  if (!props.gap) return 'gap-4';
  const gapClasses: Record<SpaceScale, string> = {
    '0': '',
    '025': 'gap-[1px]',
    '050': 'gap-[2px]',
    '100': 'gap-[4px]',
    '150': 'gap-[6px]',
    '200': 'gap-[8px]',
    '300': 'gap-[12px]',
    '400': 'gap-[16px]',
    '500': 'gap-[20px]',
  };
  return gapClasses[props.gap];
});

const alignmentClasses = computed(() => {
  const alignMap: Record<Align, string> = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    'space-around': 'justify-around',
    'space-between': 'justify-between',
    'space-evenly': 'justify-evenly',
  };

  const blockAlignMap: Record<BlockAlign, string> = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    baseline: 'items-baseline',
    stretch: 'items-stretch',
  };

  return {
    [alignMap[props.align || 'start']]: !!props.align,
    [blockAlignMap[props.blockAlign || 'start']]: !!props.blockAlign,
  };
});

const directionClasses = computed(() => {
  const directionMap: Record<Direction, string> = {
    row: 'flex-row',
    'row-reverse': 'flex-row-reverse',
  };

  return directionMap[props.direction || 'row'];
});

const inlineStackClasses = computed(() => {
  return {
    flex: true,
    [directionClasses.value]: true,
    [flexGap.value]: true,
    'flex-wrap': wrap,
    'flex-nowrap': !wrap,
    'w-full': props.fullWidth,
    'h-full': props.fullHeight,
    ...alignmentClasses.value,
  };
});
</script>

<template>
  <component :is="as" :class="inlineStackClasses">
    <slot />
  </component>
</template>
